import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Upload, FileText, Check, X, Link, Plus, Eye } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function XmlImport() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Modal states
  const [selectedImport, setSelectedImport] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editedItems, setEditedItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    fetchImports();
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchImports = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/xml-imports`);
      setImports(res.data.data || res.data);
    } catch (error) {
      toast.error("Erro ao carregar importações");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { params: { per_page: 1000 } });
      setProducts(res.data.data || res.data);
    } catch (error) {
      console.log("Erro ao carregar produtos");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data.data || res.data);
    } catch (error) {
      console.log("Erro ao carregar categorias");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error("Por favor, selecione um arquivo XML");
      return;
    }

    const formData = new FormData();
    formData.append('xml_file', file);

    try {
      setUploading(true);
      const res = await axios.post(`${API}/xml-imports`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("XML importado com sucesso!");
      fetchImports();
      
      // Abrir modal com o resultado
      if (res.data.data) {
        openModal(res.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao importar XML");
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const openModal = async (importData) => {
    try {
      const res = await axios.get(`${API}/xml-imports/${importData.id}`);
      setSelectedImport(res.data);
      setEditedItems(res.data.items?.map(item => ({
        ...item,
        quantidade: item.quantidade,
        valor_unitario: item.valor_unitario,
      })) || []);
      setActiveTab(0);
      setModalOpen(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes");
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImport(null);
    setEditedItems([]);
    setActiveTab(0);
  };

  const handleItemChange = (itemId, field, value) => {
    setEditedItems(items => 
      items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleMapProduct = async (itemId, productId) => {
    try {
      await axios.post(`${API}/xml-import-items/${itemId}/map`, { product_id: productId });
      toast.success("Produto vinculado!");
      
      setEditedItems(items =>
        items.map(item =>
          item.id === itemId ? { ...item, product_id: productId, is_mapped: true } : item
        )
      );
    } catch (error) {
      toast.error("Erro ao vincular produto");
    }
  };

  const handleCreateProduct = async (item) => {
    try {
      const res = await axios.post(`${API}/xml-import-items/${item.id}/create-product`, {
        category_id: item.category_id || null,
        sale_price: item.valor_unitario * 1.3, // 30% margem default
      });
      toast.success("Produto criado e vinculado!");
      
      setEditedItems(items =>
        items.map(i =>
          i.id === item.id ? { ...i, product_id: res.data.product.id, is_mapped: true } : i
        )
      );
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao criar produto");
    }
  };

  const handleApproveItems = async () => {
    const unmapped = editedItems.filter(i => !i.is_mapped && !i.product_id);
    if (unmapped.length > 0) {
      toast.error(`Ainda existem ${unmapped.length} item(ns) não vinculados`);
      return;
    }

    try {
      setApproving(true);
      await axios.post(`${API}/xml-imports/${selectedImport.id}/approve-items`, {
        items: editedItems.map(item => ({
          id: item.id,
          quantidade: parseFloat(item.quantidade) || 0,
          valor_unitario: parseFloat(item.valor_unitario) || 0,
        }))
      });
      toast.success("Itens aprovados! Estoque atualizado.");
      fetchImports();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao aprovar itens");
    } finally {
      setApproving(false);
    }
  };

  const handleApproveBills = async () => {
    try {
      setApproving(true);
      await axios.post(`${API}/xml-imports/${selectedImport.id}/approve-bills`);
      toast.success("Boletos aprovados! Contas a pagar criadas.");
      fetchImports();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao aprovar boletos");
    } finally {
      setApproving(false);
    }
  };

  const formatMoney = (value) => 
    `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'warning';
      case 'items_approved': return 'info';
      case 'bills_approved': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Rascunho';
      case 'items_approved': return 'Itens Aprovados';
      case 'bills_approved': return 'Boletos Aprovados';
      case 'completed': return 'Concluído';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Lançando (Importação XML)
        </Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
          disabled={uploading}
          data-testid="upload-xml-btn"
        >
          {uploading ? "Importando..." : "Importar XML de NF-e"}
          <input type="file" accept=".xml" hidden onChange={handleFileUpload} />
        </Button>
      </Box>

      {/* Lista de Importações */}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : imports.length === 0 ? (
          <Alert severity="info">
            Nenhuma importação de XML encontrada. Clique em "Importar XML de NF-e" para começar.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nº Nota</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell>Data Emissão</TableCell>
                  <TableCell align="right">Valor Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imports.map((imp) => (
                  <TableRow key={imp.id} data-testid={`xml-import-row-${imp.id}`}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FileText size={16} />
                        {imp.numero_nota || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>{imp.supplier?.name || 'Não identificado'}</TableCell>
                    <TableCell>{formatDate(imp.data_emissao)}</TableCell>
                    <TableCell align="right">{formatMoney(imp.valor_total)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(imp.status)} 
                        color={getStatusColor(imp.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        onClick={() => openModal(imp)}
                        data-testid={`view-import-${imp.id}`}
                      >
                        <Eye size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modal de Detalhes com Tabs */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">
              NF-e {selectedImport?.numero_nota} - {selectedImport?.supplier?.name || 'Fornecedor não identificado'}
            </Typography>
            <Chip 
              label={getStatusLabel(selectedImport?.status)} 
              color={getStatusColor(selectedImport?.status)} 
              size="small" 
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab label={`Itens (${editedItems.length})`} data-testid="tab-items" />
            <Tab label={`Boletos (${selectedImport?.bills?.length || 0})`} data-testid="tab-bills" />
          </Tabs>

          {/* Tab 1 - Itens */}
          {activeTab === 0 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell>UN</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Vlr Unit.</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Produto Vinculado</TableCell>
                    <TableCell align="center">Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedItems.map((item) => (
                    <TableRow key={item.id} sx={{ bgcolor: item.is_mapped ? 'success.50' : 'warning.50' }}>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={item.descricao}>
                          {item.descricao}
                        </Typography>
                        {item.ean && <Typography variant="caption" color="text.secondary">EAN: {item.ean}</Typography>}
                      </TableCell>
                      <TableCell>{item.unidade}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantidade}
                          onChange={(e) => handleItemChange(item.id, 'quantidade', e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ step: "0.01", min: "0" }}
                          disabled={selectedImport?.status !== 'draft'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={item.valor_unitario}
                          onChange={(e) => handleItemChange(item.id, 'valor_unitario', e.target.value)}
                          sx={{ width: 100 }}
                          inputProps={{ step: "0.01", min: "0" }}
                          disabled={selectedImport?.status !== 'draft'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney((item.quantidade || 0) * (item.valor_unitario || 0))}
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        {item.is_mapped || item.product_id ? (
                          <Chip icon={<Check size={14} />} label={item.product?.name || 'Vinculado'} color="success" size="small" />
                        ) : (
                          <Autocomplete
                            size="small"
                            options={products}
                            getOptionLabel={(opt) => opt.name || ''}
                            onChange={(e, val) => val && handleMapProduct(item.id, val.id)}
                            renderInput={(params) => <TextField {...params} placeholder="Buscar produto..." />}
                            sx={{ minWidth: 180 }}
                            disabled={selectedImport?.status !== 'draft'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!item.is_mapped && !item.product_id && selectedImport?.status === 'draft' && (
                          <Button
                            size="small"
                            startIcon={<Plus size={14} />}
                            onClick={() => handleCreateProduct(item)}
                          >
                            Criar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 2 - Boletos */}
          {activeTab === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nº Duplicata</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedImport?.bills?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">Nenhuma duplicata encontrada no XML</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedImport?.bills?.map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell>{bill.numero_duplicata || '-'}</TableCell>
                        <TableCell>{formatDate(bill.data_vencimento)}</TableCell>
                        <TableCell align="right">{formatMoney(bill.valor)}</TableCell>
                        <TableCell>
                          {bill.is_approved ? (
                            <Chip icon={<Check size={14} />} label="Aprovado" color="success" size="small" />
                          ) : (
                            <Chip label="Pendente" color="warning" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={closeModal} color="inherit">
            Fechar
          </Button>
          {activeTab === 0 && selectedImport?.status === 'draft' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleApproveItems}
              disabled={approving}
              startIcon={approving ? <CircularProgress size={16} /> : <Check size={16} />}
              data-testid="approve-items-btn"
            >
              Aprovar Itens e Dar Entrada no Estoque
            </Button>
          )}
          {activeTab === 1 && selectedImport?.bills?.length > 0 && 
           !selectedImport?.bills?.every(b => b.is_approved) && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleApproveBills}
              disabled={approving}
              startIcon={approving ? <CircularProgress size={16} /> : <Check size={16} />}
              data-testid="approve-bills-btn"
            >
              Aprovar Boletos e Criar Contas a Pagar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
